import { useState, useRef, useMemo } from "react";
import { createPortal } from "react-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Upload, Pencil, Trash2, Search, X, Loader2, UserRound, CheckCircle2, FileDown, Camera, Info } from "lucide-react";
import { api, apiError, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import FeatureToggles, { DEFAULT_FEATURES, normalizeFeatures } from "@/components/FeatureToggles";

const tabBtn = (on) => `flex-1 h-9 rounded-md text-sm font-semibold transition ${on ? "bg-white text-indigo-600 shadow-sm" : "text-slate-500 hover:text-slate-700"}`;

function Modal({ open, onClose, title, children }) {
  if (!open) return null;
  return createPortal(
    <div className="fixed inset-0 z-[100]">
      <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm" onClick={onClose} />
      <div className="fixed inset-0 overflow-y-auto">
        <div className="min-h-full flex items-center justify-center p-4">
          <div className="relative w-full max-w-lg bg-white rounded-2xl shadow-2xl border border-slate-200 my-4">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
              <h3 className="font-head text-lg font-bold text-slate-900">{title}</h3>
              <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="modal-close-btn">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="px-6 py-5">{children}</div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

const empty = { staff_id: "", name: "", pin: "", role_title: "Staff", site_id: "", business_id: "", authorized_site_ids: [], timezone: "Asia/Muscat", email: "", duty_start: "09:00 AM", duty_end: "05:00 PM", grace_minutes: 15, off_days: ["Friday", "Saturday"], can_leave_desk: true, features: { ...DEFAULT_FEATURES } };

const TZ_OPTIONS = [
  "Asia/Muscat", "Asia/Dubai", "Asia/Riyadh", "Asia/Qatar", "Asia/Bahrain", "Asia/Kuwait",
  "Asia/Karachi", "Asia/Kolkata", "Asia/Dhaka", "Asia/Manila",
  "Europe/London", "Europe/Paris", "America/New_York", "America/Los_Angeles", "UTC",
];

const DAY_DEFS = [
  ["Monday", "Mon"],
  ["Tuesday", "Tue"],
  ["Wednesday", "Wed"],
  ["Thursday", "Thu"],
  ["Friday", "Fri"],
  ["Saturday", "Sat"],
  ["Sunday", "Sun"],
];

export default function Employees() {
  const qc = useQueryClient();
  const { me } = useAuth();
  const canEdit = me?.can_edit;
  const canDelete = me?.can_delete;
  const fileRef = useRef();
  const [search, setSearch] = useState("");
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [err, setErr] = useState("");
  const [masterPath, setMasterPath] = useState("");
  const photoRef = useRef();
  const [tab, setTab] = useState("details");

  const { data: employees = [], isLoading } = useQuery({ queryKey: ["employees"], queryFn: () => api.get("/admin/employees") });
  const { data: sites = [] } = useQuery({ queryKey: ["sites"], queryFn: () => api.get("/admin/sites") });
  const { data: businesses = [] } = useQuery({ queryKey: ["businesses"], queryFn: () => api.get("/admin/businesses") });
  const scopedBusiness = me?.role !== "super_admin" && !!me?.business_id;
  const siteName = (id) => sites.find((s) => s.id === id)?.name || "—";

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["employees"] });
    qc.invalidateQueries({ queryKey: ["attendance"] });
    qc.invalidateQueries({ queryKey: ["quota"] });
  };

  const saveMut = useMutation({
    mutationFn: (body) =>
      editing ? api.put(`/admin/employees/${editing.id}`, body) : api.post("/admin/employees", body),
    onSuccess: () => {
      toast.success(editing ? "Employee updated" : "Employee added");
      setModal(false);
      invalidate();
    },
    onError: (e) => setErr(apiError(e)),
  });

  const delMut = useMutation({
    mutationFn: (id) => api.delete(`/admin/employees/${id}`),
    onSuccess: () => {
      toast.success("Employee removed");
      invalidate();
    },
    onError: (e) => toast.error(apiError(e)),
  });

  const bulkMut = useMutation({
    mutationFn: (file_base64) => api.post("/admin/employees/bulk", { file_base64 }),
    onSuccess: (r) => {
      toast.success(`Imported ${r.created} employees (${r.skipped} skipped)`);
      if (r.errors?.length) toast.warning(r.errors.slice(0, 3).join(" • "));
      invalidate();
    },
    onError: (e) => toast.error(apiError(e)),
  });

  const photoMut = useMutation({
    mutationFn: (photo_base64) => api.post(`/admin/employees/${editing.id}/master-photo`, { photo_base64 }),
    onSuccess: (r) => {
      toast.success("Master photo updated");
      setMasterPath(r.master_photo_path);
      setEditing((prev) => (prev ? { ...prev, has_master_photo: true, master_photo_path: r.master_photo_path } : prev));
      invalidate();
    },
    onError: (e) => toast.error(apiError(e)),
  });

  const onPhoto = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please choose an image file");
      e.target.value = "";
      return;
    }
    const reader = new FileReader();
    reader.onload = () => photoMut.mutate(reader.result);
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  const openAdd = () => {
    setEditing(null);
    setForm(empty);
    setMasterPath("");
    setTab("details");
    setErr("");
    setModal(true);
  };
  const openEdit = (emp) => {
    setEditing(emp);
    setMasterPath(emp.has_master_photo ? emp.master_photo_path : "");
    setTab("details");
    setForm({ staff_id: emp.staff_id, name: emp.name, pin: "", role_title: emp.role_title || "Staff", site_id: emp.site_id || "", business_id: emp.business_id || "", authorized_site_ids: emp.authorized_site_ids && emp.authorized_site_ids.length ? emp.authorized_site_ids : (emp.site_id ? [emp.site_id] : []), timezone: emp.timezone || "Asia/Muscat", email: emp.email || "", duty_start: emp.duty_start || "09:00 AM", duty_end: emp.duty_end || "05:00 PM", grace_minutes: emp.grace_minutes ?? 15, off_days: emp.off_days || ["Friday", "Saturday"], can_leave_desk: !!emp.can_leave_desk, features: emp.features ? normalizeFeatures(emp.features) : { ...DEFAULT_FEATURES, leave_desk: !!emp.can_leave_desk } });
    setErr("");
    setModal(true);
  };

  const submit = (e) => {
    e.preventDefault();
    setErr("");
    if (!scopedBusiness && !form.business_id) { setTab("details"); setErr("Please select a Business for this employee."); return; }
    const body = { ...form, site_id: form.site_id || null, email: form.email || null, grace_minutes: parseInt(form.grace_minutes, 10) || 0 };
    if (scopedBusiness) delete body.business_id;
    if (editing && !body.pin) delete body.pin;
    saveMut.mutate(body);
  };

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => bulkMut.mutate(reader.result);
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  const downloadTemplate = async () => {
    try {
      const blob = await api.get("/admin/employees/template", { responseType: "blob" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "employees_bulk_upload_template.xlsx";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return employees.filter((e) => e.name.toLowerCase().includes(q) || e.staff_id.toLowerCase().includes(q));
  }, [employees, search]);

  return (
    <div className="space-y-5 animate-fade-up" data-testid="employees-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <p className="text-sm text-slate-500">{employees.length} employees registered</p>
        </div>
        <div className="flex items-center gap-2">
          <input ref={fileRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={onFile} data-testid="employee-excel-input" />
          <button
            onClick={downloadTemplate}
            data-testid="employee-template-btn"
            className="inline-flex items-center gap-2 h-10 px-4 rounded-lg border border-slate-200 bg-white text-sm font-semibold text-slate-700 hover:bg-slate-50 transition"
          >
            <FileDown className="h-4 w-4" /> Template
          </button>
          <button
            onClick={() => fileRef.current?.click()}
            disabled={bulkMut.isPending}
            data-testid="employee-excel-upload-btn"
            className="inline-flex items-center gap-2 h-10 px-4 rounded-lg border border-slate-200 bg-white text-sm font-semibold text-slate-700 hover:bg-slate-50 transition"
          >
            {bulkMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
            Bulk Upload
          </button>
          <button
            onClick={openAdd}
            data-testid="employee-add-btn"
            className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 transition shadow-sm shadow-indigo-600/20"
          >
            <Plus className="h-4 w-4" /> Add Employee
          </button>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
        <div className="p-4 border-b border-slate-100">
          <div className="relative max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input
              data-testid="employee-search-input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search name or staff ID…"
              className="w-full h-10 rounded-lg border border-slate-200 pl-10 pr-3 text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
            />
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs font-semibold uppercase text-slate-400 border-b border-slate-100 bg-slate-50/50">
                <th className="px-4 py-3">Employee</th>
                <th className="px-4 py-3">Staff ID</th>
                <th className="px-4 py-3">Role</th>
                <th className="px-4 py-3">Site</th>
                <th className="px-4 py-3">Duty Hours</th>
                <th className="px-4 py-3">Master Photo</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50" data-testid="employees-table-body">
              {isLoading ? (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-400"><Loader2 className="h-5 w-5 animate-spin inline" /></td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-400">No employees found</td></tr>
              ) : (
                filtered.map((e) => (
                  <tr key={e.id} className="hover:bg-slate-50/60 transition" data-testid={`employee-row-${e.staff_id}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="grid place-items-center h-9 w-9 rounded-full bg-indigo-50 text-indigo-600 text-xs font-bold">
                          {e.name.split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase()}
                        </div>
                        <span className="font-medium text-slate-800">{e.name}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-slate-600">{e.staff_id}</td>
                    <td className="px-4 py-3 text-slate-600">{e.role_title || "Staff"}</td>
                    <td className="px-4 py-3 text-slate-600">{siteName(e.site_id)}</td>
                    <td className="px-4 py-3 text-slate-600 whitespace-nowrap" data-testid={`employee-duty-${e.staff_id}`}>
                      <div className="font-mono text-xs">{e.duty_start || "09:00 AM"} – {e.duty_end || "05:00 PM"} <span className="text-slate-400">(+{e.grace_minutes ?? 15}m)</span></div>
                      <div className="text-[11px] text-indigo-500 mt-0.5">Off: {(e.off_days && e.off_days.length) ? e.off_days.map((d) => d.slice(0, 3)).join(", ") : "None"}</div>
                    </td>
                    <td className="px-4 py-3">
                      {e.has_master_photo ? (
                        <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-600"><CheckCircle2 className="h-3.5 w-3.5" /> Set</span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-xs text-slate-400"><UserRound className="h-3.5 w-3.5" /> None</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        {canEdit && (
                          <button onClick={() => openEdit(e)} data-testid={`employee-edit-${e.staff_id}`} className="h-8 w-8 grid place-items-center rounded-lg text-slate-500 hover:bg-slate-100 hover:text-indigo-600 transition">
                            <Pencil className="h-4 w-4" />
                          </button>
                        )}
                        {canDelete && (
                          <button onClick={() => { if (window.confirm(`Remove ${e.name}?`)) delMut.mutate(e.id); }} data-testid={`employee-delete-${e.staff_id}`} className="h-8 w-8 grid place-items-center rounded-lg text-slate-500 hover:bg-rose-50 hover:text-rose-600 transition">
                            <Trash2 className="h-4 w-4" />
                          </button>
                        )}
                        {!canEdit && !canDelete && <span className="text-xs text-slate-300">—</span>}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Modal open={modal} onClose={() => setModal(false)} title={editing ? "Edit Employee" : "Add Employee"}>
        <form onSubmit={submit} className="space-y-4" data-testid="employee-form">
          <div className="flex gap-1 rounded-lg bg-slate-100 p-1">
            <button type="button" data-testid="emp-tab-details" onClick={() => setTab("details")} className={tabBtn(tab === "details")}>Details</button>
            <button type="button" data-testid="emp-tab-features" onClick={() => setTab("features")} className={tabBtn(tab === "features")}>Feature Permissions</button>
          </div>

          {tab === "details" && (
          <div className="space-y-4">
          {editing && (
            <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-4" data-testid="master-photo-section">
              <label className="block text-xs font-semibold text-slate-500 uppercase mb-3">Master Photo (AI Face Verification)</label>
              <div className="flex items-center gap-4">
                <div className="h-20 w-20 flex-shrink-0 rounded-xl overflow-hidden border border-slate-200 bg-white grid place-items-center">
                  {masterPath ? (
                    <img src={fileUrl(masterPath)} alt="Master" className="h-full w-full object-cover" data-testid="master-photo-preview" />
                  ) : (
                    <UserRound className="h-8 w-8 text-slate-300" data-testid="master-photo-empty" />
                  )}
                </div>
                <div className="flex-1">
                  <input ref={photoRef} type="file" accept="image/*" className="hidden" onChange={onPhoto} data-testid="master-photo-input" />
                  <button
                    type="button"
                    onClick={() => photoRef.current?.click()}
                    disabled={photoMut.isPending}
                    data-testid="master-photo-upload-btn"
                    className="inline-flex items-center gap-2 h-9 px-4 rounded-lg bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 disabled:opacity-60 transition"
                  >
                    {photoMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Camera className="h-4 w-4" />}
                    {masterPath ? "Change Photo" : "Upload Photo"}
                  </button>
                  <p className="mt-2 flex items-start gap-1.5 text-[11px] leading-snug text-slate-500">
                    <Info className="h-3.5 w-3.5 flex-shrink-0 mt-px text-indigo-500" />
                    <span>Use a clear, front-facing photo with a plain background and good lighting for best AI face-match results. This becomes the master image used to verify clock-ins in the mobile app.</span>
                  </p>
                </div>
              </div>
            </div>
          )}
          <div className="grid grid-cols-2 gap-4">
            <Field label="Staff ID" value={form.staff_id} onChange={(v) => setForm({ ...form, staff_id: v })} testid="form-staff-id" required />
            <Field label="Full Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="form-name" required />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Field label={editing ? "PIN (leave blank to keep)" : "4-digit PIN"} value={form.pin} onChange={(v) => setForm({ ...form, pin: v.replace(/\D/g, "").slice(0, 4) })} testid="form-pin" required={!editing} placeholder="####" />
            <Field label="Role Title" value={form.role_title} onChange={(v) => setForm({ ...form, role_title: v })} testid="form-role" />
          </div>
          <Field label="Email (optional)" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="form-email" type="email" />
          <div>
            {!scopedBusiness && (
              <>
                <label className="block text-xs font-semibold text-slate-500 uppercase mb-1.5">Business Name <span className="text-rose-500">*</span></label>
                <select
                  data-testid="form-business"
                  value={form.business_id}
                  onChange={(e) => setForm({ ...form, business_id: e.target.value })}
                  required
                  className="w-full h-11 rounded-lg border border-slate-200 bg-white px-3 text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
                >
                  <option value="">Select a business…</option>
                  {businesses.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
                {businesses.length === 0 && <p className="mt-1.5 text-xs text-amber-600">No businesses yet — create one under Businesses first.</p>}
              </>
            )}
          </div>
          <div>
            <select
              data-testid="form-site"
              value={form.site_id}
              onChange={(e) => setForm({ ...form, site_id: e.target.value })}
              className="w-full h-11 rounded-lg border border-slate-200 bg-white px-3 text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
            >
              <option value="">Unassigned</option>
              {sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>

          {me?.role === "super_admin" && (
            <div data-testid="form-authorized-sites-wrap">
              <label className="block text-xs font-semibold text-slate-500 uppercase mb-1.5">Authorized Sites (staff can clock into)</label>
              {sites.length === 0 ? (
                <p className="text-xs text-slate-400">No sites yet. Add a site first.</p>
              ) : (
                <div className="flex flex-wrap gap-1.5" data-testid="form-authorized-sites">
                  {sites.map((s) => {
                    const on = (form.authorized_site_ids || []).includes(s.id);
                    return (
                      <button
                        key={s.id}
                        type="button"
                        data-testid={`form-authorized-site-${s.id}`}
                        aria-pressed={on}
                        onClick={() => {
                          const set = new Set(form.authorized_site_ids || []);
                          on ? set.delete(s.id) : set.add(s.id);
                          setForm({ ...form, authorized_site_ids: Array.from(set) });
                        }}
                        className={`px-3 h-9 rounded-lg text-xs font-semibold border transition-colors ${on ? "bg-indigo-600 border-indigo-600 text-white" : "bg-white border-slate-200 text-slate-500 hover:border-indigo-300 hover:text-indigo-600"}`}
                      >
                        {s.name}
                      </button>
                    );
                  })}
                </div>
              )}
              <p className="mt-1.5 text-xs text-slate-400">Select every site this employee may clock in/out at. If only one is selected, the mobile app picks it automatically. The primary site is always included.</p>
            </div>
          )}

          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase mb-1.5">Timezone</label>
            <select
              data-testid="form-timezone"
              value={form.timezone}
              onChange={(e) => setForm({ ...form, timezone: e.target.value })}
              className="w-full h-11 rounded-lg border border-slate-200 bg-white px-3 text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
            >
              {TZ_OPTIONS.map((tz) => <option key={tz} value={tz}>{tz.replace("_", " ")}</option>)}
            </select>
            <p className="mt-1.5 text-xs text-slate-400">Used for this employee's report times and durations. Oman staff use Asia/Muscat.</p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <TimeSelect label="Duty Start" value={form.duty_start} onChange={(v) => setForm({ ...form, duty_start: v })} testid="form-duty-start" />
            <TimeSelect label="Duty End" value={form.duty_end} onChange={(v) => setForm({ ...form, duty_end: v })} testid="form-duty-end" />
          </div>
          <Field label="Grace (minutes)" value={form.grace_minutes} onChange={(v) => setForm({ ...form, grace_minutes: v.replace(/\D/g, "").slice(0, 3) })} testid="form-grace" type="number" placeholder="15" />

          <DaysPicker label="Weekly Off Days" value={form.off_days} onChange={(v) => setForm({ ...form, off_days: v })} testid="form-off-days" />

          </div>
          )}

          {tab === "features" && (
            <FeatureToggles value={form.features} onChange={(f) => setForm({ ...form, features: f, can_leave_desk: f.leave_desk })} />
          )}

          {err && <p className="text-sm text-rose-600 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2">{err}</p>}
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={() => setModal(false)} className="h-10 px-4 rounded-lg border border-slate-200 text-sm font-semibold text-slate-600 hover:bg-slate-50">Cancel</button>
            <button type="submit" disabled={saveMut.isPending} data-testid="employee-save-btn" className="h-10 px-5 rounded-lg bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 disabled:opacity-60 flex items-center gap-2">
              {saveMut.isPending && <Loader2 className="h-4 w-4 animate-spin" />} {editing ? "Save Changes" : "Add Employee"}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}

function Field({ label, value, onChange, testid, type = "text", required, placeholder }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-slate-500 uppercase mb-1.5">{label}</label>
      <input
        data-testid={testid}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        placeholder={placeholder}
        className="w-full h-11 rounded-lg border border-slate-200 bg-white px-3 text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
      />
    </div>
  );
}

const HOURS = Array.from({ length: 12 }, (_, i) => String(i + 1).padStart(2, "0"));
const MINUTES = Array.from({ length: 12 }, (_, i) => String(i * 5).padStart(2, "0"));

function parseTime(str) {
  const m = /^(\d{1,2}):(\d{2})\s?(AM|PM)$/i.exec((str || "").trim());
  if (!m) return { h: "09", mi: "00", mer: "AM" };
  return { h: m[1].padStart(2, "0"), mi: m[2], mer: m[3].toUpperCase() };
}

function DaysPicker({ label, value, onChange, testid }) {
  const selected = new Set(value || []);
  const toggle = (day) => {
    const next = new Set(selected);
    next.has(day) ? next.delete(day) : next.add(day);
    onChange(DAY_DEFS.map(([d]) => d).filter((d) => next.has(d)));
  };
  return (
    <div>
      <label className="block text-xs font-semibold text-slate-500 uppercase mb-1.5">{label}</label>
      <div className="flex flex-wrap gap-1.5" data-testid={testid}>
        {DAY_DEFS.map(([day, short]) => {
          const on = selected.has(day);
          return (
            <button
              key={day}
              type="button"
              data-testid={`${testid}-${day.toLowerCase()}`}
              aria-pressed={on}
              onClick={() => toggle(day)}
              className={`px-3 h-9 rounded-lg text-xs font-semibold border transition-colors ${
                on
                  ? "bg-indigo-600 border-indigo-600 text-white"
                  : "bg-white border-slate-200 text-slate-500 hover:border-indigo-300 hover:text-indigo-600"
              }`}
            >
              {short}
            </button>
          );
        })}
      </div>
      <p className="mt-1.5 text-xs text-slate-400">On off days, staff are not flagged Late or Absent.</p>
    </div>
  );
}

function TimeSelect({ label, value, onChange, testid }) {
  const { h, mi, mer } = parseTime(value);
  const emit = (nh, nmi, nmer) => onChange(`${nh}:${nmi} ${nmer}`);
  const cls =
    "h-11 rounded-lg border border-slate-200 bg-white px-2 text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100";
  return (
    <div>
      <label className="block text-xs font-semibold text-slate-500 uppercase mb-1.5">{label}</label>
      <div className="grid grid-cols-3 gap-1.5" data-testid={testid}>
        <select data-testid={`${testid}-hour`} value={h} onChange={(e) => emit(e.target.value, mi, mer)} className={cls}>
          {HOURS.map((x) => <option key={x} value={x}>{x}</option>)}
        </select>
        <select data-testid={`${testid}-min`} value={mi} onChange={(e) => emit(h, e.target.value, mer)} className={cls}>
          {MINUTES.map((x) => <option key={x} value={x}>{x}</option>)}
        </select>
        <select data-testid={`${testid}-mer`} value={mer} onChange={(e) => emit(h, mi, e.target.value)} className={cls}>
          <option value="AM">AM</option>
          <option value="PM">PM</option>
        </select>
      </div>
    </div>
  );
}
